/**
 * Server Health Check Component
 * Display server connection status and health
 */

import React, { useState, useEffect } from 'react';
import { useProcessingStore } from '../store/useProcessingStore';
import { joinUrl } from '../utils/url';
import { Server, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface HealthStatus {
  status: 'ok' | 'error' | 'checking';
  timestamp?: string;
  environment?: string;
  kv_configured?: boolean;
  r2_configured?: boolean;
  message?: string;
  responseTime?: number;
}

export const ServerHealthCheck: React.FC = () => {
  const { serverUrl } = useProcessingStore();
  const [health, setHealth] = useState<HealthStatus>({ status: 'checking' });
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [checking, setChecking] = useState(false);

  const checkHealth = async () => {
    setChecking(true);
    const startTime = Date.now();
    const healthUrl = joinUrl(serverUrl, '/health');

    // Debug: Log the URL being requested
    console.log('[ServerHealthCheck] Requesting:', healthUrl);
    console.log('[ServerHealthCheck] serverUrl from store:', serverUrl);

    try {
      const response = await axios.get(healthUrl, {
        timeout: 10000,
      });

      const responseTime = Date.now() - startTime;
      console.log('[ServerHealthCheck] Success:', response.data);

      setHealth({
        status: 'ok',
        timestamp: response.data.timestamp,
        environment: response.data.environment,
        kv_configured: response.data.kv_configured,
        r2_configured: response.data.r2_configured,
        responseTime,
      });
      setLastCheck(new Date());
    } catch (error: any) {
      console.error('[ServerHealthCheck] Error:', error);
      console.error('[ServerHealthCheck] Error response:', error.response);
      console.error('[ServerHealthCheck] Error status:', error.response?.status);
      console.error('[ServerHealthCheck] Error URL:', error.config?.url);

      setHealth({
        status: 'error',
        message: `${error.response?.status || 'Network Error'}: ${error.response?.data?.message || error.message || '无法连接到服务器'}`,
      });
      setLastCheck(new Date());
    } finally {
      setChecking(false);
    }
  };

  // Auto-check on mount
  useEffect(() => {
    checkHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverUrl]);

  const getStatusIcon = () => {
    switch (health.status) {
      case 'ok':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'checking':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = () => {
    switch (health.status) {
      case 'ok':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'checking':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusText = () => {
    switch (health.status) {
      case 'ok':
        return '服务器运行正常';
      case 'error':
        return '服务器连接失败';
      case 'checking':
        return '正在检查服务器...';
      default:
        return '服务器状态未知';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${getStatusColor()} transition-all`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Server className="w-6 h-6 text-gray-600" />
          <div>
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className="font-semibold text-gray-800">
                {getStatusText()}
              </span>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              <span className="font-mono text-xs">
                {serverUrl}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={checkHealth}
          disabled={checking}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">重新检查</span>
        </button>
      </div>

      {/* Detailed Status */}
      {health.status === 'ok' && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-green-200">
          <div>
            <p className="text-xs text-gray-500">环境</p>
            <p className="text-sm font-medium text-gray-800">
              {health.environment || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">响应时间</p>
            <p className="text-sm font-medium text-gray-800">
              {health.responseTime}ms
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">KV 存储</p>
            <p className="text-sm font-medium">
              {health.kv_configured ? (
                <span className="text-green-600">✓ 已配置</span>
              ) : (
                <span className="text-red-600">✗ 未配置</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">R2 存储</p>
            <p className="text-sm font-medium">
              {health.r2_configured ? (
                <span className="text-green-600">✓ 已配置</span>
              ) : (
                <span className="text-red-600">✗ 未配置</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {health.status === 'error' && health.message && (
        <div className="mt-4 pt-4 border-t border-red-200">
          <p className="text-sm text-red-700">
            <span className="font-semibold">错误详情：</span>
            {health.message}
          </p>
          <p className="text-xs text-red-600 mt-2">
            请检查：
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Worker 是否已部署到 Cloudflare</li>
              <li>Worker URL 是否正确配置</li>
              <li>网络连接是否正常</li>
              <li>CORS 设置是否正确</li>
            </ul>
          </p>
        </div>
      )}

      {/* Last Check Time */}
      {lastCheck && (
        <div className="mt-3 text-xs text-gray-500">
          上次检查: {lastCheck.toLocaleTimeString('zh-CN')}
        </div>
      )}
    </div>
  );
};
